import { styled } from '@linaria/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useReducer, useState } from 'react';
import {
  getIntegrationEntries,
  useIntegrationEntries,
} from '../../../../api/integrations-client/integrations-client.hooks';
import type { IntegrationEntry } from '../../../../api/integrations-client/integrations-client.type';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../../components/loader/branded-loader';
import { useOrganizationId } from '../../../../helpers/auth-helpers/auth.hooks';
import { useToast } from '../../../notifications/notifications.hooks';
import { SubprojectInfoPanel } from '../subproject-info-panel';
import {
  useAddSubprojectToBoardSetting,
  useBoardSettings,
  useCreateBoardSetting,
  useRemoveSubprojectFromBoardSetting,
} from './board-settings-client.hooks';
import type { BoardSetting, CreateBoardSettingPayload } from './board-settings-client.type';
import { BoardSettingMethodology } from './board-settings-client.type';
import { BoardSettingsList } from './board-settings-list';
import { BoardSettingsPanel } from './board-settings-panel';
import { boardSettingReducer } from './board-settings-reducer';
import { collectSubprojects } from './board-settings.helpers';
import { CreateBoardSettings } from './create-board-settings';
import { EditBoardSettingsPanel, type PendingSubprojectChanges } from './edit-board-settings-panel';

export function BoardSettings() {
  const i18n = useTranslation();
  const organizationId = useOrganizationId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pageState, setPageState] = useState<'update' | 'create' | 'idle'>('idle');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSubprojectId, setSelectedSubprojectId] = useState<string | null>(null);
  const [selectedBoardSettingId, setSelectedBoardSettingId] = useState<string | null>(null);
  const [pendingSubprojectChanges, setPendingSubprojectChanges] = useState<PendingSubprojectChanges>({
    toAdd: [],
    toRemove: [],
  });
  const [pendingChanges, setPendingChanges] = useState<Partial<CreateBoardSettingPayload>>({});

  const [state, dispatch] = useReducer(boardSettingReducer, {} as BoardSetting);

  const { data: boardSettings = [], query: boardSettingsQuery } = useBoardSettings({ organization_id: organizationId });
  const { addSubprojectsToBoardSetting, isAdding } = useAddSubprojectToBoardSetting();
  const { removeSubprojectsFromBoardSetting, isRemoving } = useRemoveSubprojectFromBoardSetting();

  const { useCreateBoardSettingHook: createBoardSettingMutation } = useCreateBoardSetting();

  const createBoardSetting = async (boardSetting: CreateBoardSettingPayload) => {
    let newBoardSetting: BoardSetting | null = null;
    let boardCreationSucceeded = false;

    try {
      // Step 1: Create the board setting
      newBoardSetting = await createBoardSettingMutation(boardSetting);
      boardCreationSucceeded = true;

      // Step 2: Map any selected subprojects to the newly created board setting
      if (pendingSubprojectChanges.toAdd.length > 0 && organizationId) {
        try {
          await addSubprojectsToBoardSetting(newBoardSetting.id, pendingSubprojectChanges.toAdd, organizationId);
          toast.showSuccess(i18n.t('data_management.board_settings.created_with_boards_success'));
        } catch (mappingError) {
          // Board setting was created, but mapping failed
          console.error('Error mapping boards to board setting:', mappingError);
          toast.showWarning(i18n.t('data_management.board_settings.created_with_mapping_error'));
        }
      } else {
        // No boards to map
        toast.showSuccess(i18n.t('data_management.board_settings.created_success'));
      }
    } catch (error) {
      // Board setting creation failed
      console.error('Error creating board setting:', error);
      toast.showError(i18n.t('data_management.board_settings.create_error'));
      return; // Don't proceed if creation failed
    } finally {
      // Always redirect to idle state if board setting was created successfully
      if (boardCreationSucceeded && newBoardSetting) {
        // Invalidate queries to mark them as stale - React Query will refetch them automatically
        // This is cleaner than manual refetch and avoids potential double-fetching
        await queryClient.invalidateQueries({ queryKey: ['board-settings'] });
        await queryClient.invalidateQueries({ queryKey: ['integration-entries'] });

        // Now set the selection - the new board should be in the list
        setSelectedBoardSettingId(newBoardSetting.id);

        // Update the reducer state with the new board setting
        dispatch({
          type: 'SET_BOARD_SETTING',
          payload: newBoardSetting,
        });

        // Clear pending changes
        setPendingChanges({});
        setPendingSubprojectChanges({ toAdd: [], toRemove: [] });

        // Return to idle state - this triggers the view change
        setPageState('idle');
      }
    }
  };

  const { data: integrationEntries = [], query: integrationEntriesQuery } = useIntegrationEntries<IntegrationEntry[]>({
    queryKey: ['integration-entries', organizationId] as const,
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      return getIntegrationEntries(organizationId);
    },
    select: (data) => data.filter((integration) => integration.projects.length > 0),
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 5,
  });

  const selectedBoardSetting = boardSettings.find((boardSetting) => boardSetting.id === selectedBoardSettingId);
  const allSubprojects = collectSubprojects(integrationEntries);
  const selectedSubproject = allSubprojects.find((subproject) => subproject.id === selectedSubprojectId);
  const selectedSubprojectIntegration = selectedSubproject
    ? integrationEntries.find((entry) => entry.subprojects.some((sub) => sub.id === selectedSubproject.id))
    : null;

  // Initialize state when page is idle and board settings are loaded
  // Skip this effect if we just created a board setting (selectedBoardSettingId is already set)
  // biome-ignore lint/correctness/useExhaustiveDependencies(state.id): state.id is used conditionally
  useEffect(() => {
    if (pageState === 'idle' && boardSettingsQuery.isSuccess && boardSettings && boardSettings.length) {
      // If there's a selectedBoardSettingId, use that - it might be a newly created board
      if (selectedBoardSettingId) {
        const found = boardSettings.find((boardSetting) => boardSetting.id === selectedBoardSettingId);
        if (found) {
          // Only update if it's different from current state
          if (state.id !== found.id) {
            dispatch({
              type: 'SET_BOARD_SETTING',
              payload: found,
            });
          }
        }
      } else if (state.id === null || state.id === undefined) {
        // No selection at all, default to first
        dispatch({
          type: 'SET_BOARD_SETTING',
          payload: boardSettings[0],
        });
        setSelectedBoardSettingId(boardSettings[0].id);
      } else {
        // We have a state.id but no selectedBoardSettingId - try to find it
        const found = boardSettings.find((boardSetting) => boardSetting.id === state.id);
        if (found) {
          setSelectedBoardSettingId(found.id);
        } else {
          // Not found, default to first
          dispatch({
            type: 'SET_BOARD_SETTING',
            payload: boardSettings[0],
          });
          setSelectedBoardSettingId(boardSettings[0].id);
        }
      }
    }
  }, [boardSettings, boardSettingsQuery.isSuccess, pageState, selectedBoardSettingId]);

  // Handle create mode
  useEffect(() => {
    if (pageState === 'create') {
      dispatch({
        type: 'SET_BOARD_SETTING',
        payload: {
          id: '',
          organization_id: organizationId || '',
          name: '',
          label_for_card_mandatory: false,
          methodology: BoardSettingMethodology.UNKNOWN,
          points_for_bug_cards: false,
          working_hours_start: '09:00',
          working_hours_end: '17:00',
        },
      });
      // Reset pending changes when entering create mode
      setPendingChanges({});
      setPendingSubprojectChanges({ toAdd: [], toRemove: [] });
    }
  }, [pageState, organizationId]);

  return (
    <BoardSettingsContainer>
      <BrandedLoadingOverlay
        visible={integrationEntriesQuery.isFetching || boardSettingsQuery.isLoading}
        transitionDuration={30}
        variant="colored"
      />
      <BoardSettingsList
        boardSettings={boardSettings}
        selectedBoardSetting={selectedBoardSetting ?? null}
        setSelectedBoardSettingId={setSelectedBoardSettingId}
        setIsEditing={setIsEditing}
        pageState={pageState}
        setPageState={setPageState}
      />
      {pageState === 'create' && (
        <>
          <CreateBoardSettings
            state={state}
            dispatch={dispatch}
            setPageState={setPageState}
            isDirty={!!state.name}
            boardSettings={boardSettings}
            createBoardSetting={createBoardSetting}
            pendingChanges={pendingChanges}
            onPendingChange={setPendingChanges}
            hasPendingBoards={pendingSubprojectChanges.toAdd.length > 0}
          />
          <EditBoardSettingsPanel
            selectedBoardSetting={state}
            subprojects={allSubprojects}
            setIsEditing={(editing) => {
              if (!editing) {
                setPageState('idle');
              }
            }}
            pendingSubprojectChanges={pendingSubprojectChanges}
            onSubprojectChange={setPendingSubprojectChanges}
            isSaving={false}
          />
        </>
      )}
      {pageState === 'idle' && (
        <>
          <BoardSettingsPanel
            subprojects={allSubprojects}
            selectedBoardSetting={selectedBoardSetting ?? null}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            selectedSubprojectId={selectedSubprojectId}
            setSelectedSubprojectId={setSelectedSubprojectId}
            pendingSubprojectChanges={pendingSubprojectChanges}
            setPendingSubprojectChanges={setPendingSubprojectChanges}
            addSubprojectsToBoardSetting={addSubprojectsToBoardSetting}
            removeSubprojectsFromBoardSetting={removeSubprojectsFromBoardSetting}
            isAdding={isAdding}
            isRemoving={isRemoving}
          />
          {isEditing && selectedBoardSetting && (
            <EditBoardSettingsPanel
              selectedBoardSetting={selectedBoardSetting}
              subprojects={allSubprojects}
              setIsEditing={setIsEditing}
              pendingSubprojectChanges={pendingSubprojectChanges}
              onSubprojectChange={setPendingSubprojectChanges}
              isSaving={isAdding || isRemoving}
            />
          )}
          {selectedSubprojectId && selectedSubproject && (
            <SubprojectInfoPanel
              subproject={selectedSubproject}
              setSelectedSubprojectId={setSelectedSubprojectId}
              integrationSource={selectedSubprojectIntegration?.integration_type ?? null}
            />
          )}
        </>
      )}
    </BoardSettingsContainer>
  );
}

const BoardSettingsContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  height: 100%;
  margin: 16px 0px 0px 0px;
`;
